import * as React from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  Cancel01Icon,
  Tick02Icon
} from '@hugeicons/core-free-icons'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'

export interface ComboboxOption {
  label: string
  value: string
  prefix?: string
}

interface BaseProps {
  options: ComboboxOption[]
  creatable?: boolean
  disabled?: boolean
  required?: boolean
  id?: string
  'aria-label'?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  onBlur?: () => void
  onFocus?: () => void
}

type SingleProps = BaseProps & {
  multiple?: false
  value: string | null
  onChange: (value: string | null) => void
}

type MultipleProps = BaseProps & {
  multiple: true
  value: string[]
  onChange: (value: string[]) => void
}

export type ComboboxProps = SingleProps | MultipleProps

export function Combobox(props: ComboboxProps) {
  const {
    options,
    creatable = false,
    disabled,
    required,
    id,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyText = 'No results found',
    onBlur,
    onFocus
  } = props

  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  const selectedValues = props.multiple
    ? props.value
    : props.value !== null
    ? [props.value]
    : []

  const labelFor = React.useCallback(
    (value: string) =>
      options.find(option => option.value === value)?.label ?? value,
    [options]
  )

  function emit(values: string[]) {
    if (props.multiple) {
      props.onChange(values)
    } else {
      props.onChange(values[0] ?? null)
    }
  }

  function toggle(value: string) {
    if (props.multiple) {
      const next = selectedValues.includes(value)
        ? selectedValues.filter(v => v !== value)
        : [...selectedValues, value]
      emit(next)
    } else {
      emit([value])
      setOpen(false)
    }
    setQuery('')
  }

  const trimmedQuery = query.trim()
  const hasExactMatch = options.some(
    option => option.label.toLowerCase() === trimmedQuery.toLowerCase()
  )
  const showCreate =
    creatable &&
    trimmedQuery.length > 0 &&
    !hasExactMatch &&
    !selectedValues.includes(trimmedQuery)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          aria-label={props['aria-label']}
          aria-required={required}
          id={id}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
          className='min-h-8 h-auto w-full justify-between gap-1 px-2 py-1 font-normal'
        >
          {props.multiple && selectedValues.length > 0 ? (
            <span className='flex flex-1 flex-wrap items-center gap-1'>
              {selectedValues.map(value => (
                <Badge key={value} variant='secondary'>
                  {labelFor(value)}
                  {/* Pointer-only shortcut: keyboard/AT users deselect via the
                      option list, and a focusable control may not nest inside
                      the trigger button */}
                  <span
                    aria-hidden='true'
                    className='text-muted-foreground hover:text-foreground cursor-pointer'
                    onClick={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      toggle(value)
                    }}
                  >
                    <HugeiconsIcon
                      icon={Cancel01Icon}
                      strokeWidth={2}
                      className='size-3'
                    />
                  </span>
                </Badge>
              ))}
            </span>
          ) : !props.multiple && props.value !== null ? (
            <span className='flex-1 truncate text-left'>
              {labelFor(props.value)}
            </span>
          ) : (
            <span className='text-muted-foreground flex-1 truncate text-left'>
              {placeholder}
            </span>
          )}
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            strokeWidth={2}
            className='size-3.5 text-muted-foreground shrink-0'
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-(--radix-popover-trigger-width) p-0'
        align='start'
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {!showCreate && <CommandEmpty>{emptyText}</CommandEmpty>}
            <CommandGroup>
              {options.map(option => {
                const isSelected = selectedValues.includes(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    onSelect={() => toggle(option.value)}
                  >
                    <span
                      className={cn(
                        'size-3.5 flex shrink-0 items-center justify-center',
                        !isSelected && 'opacity-0'
                      )}
                    >
                      <HugeiconsIcon icon={Tick02Icon} strokeWidth={2} />
                    </span>
                    {option.prefix && <span>{option.prefix}</span>}
                    <span className='flex-1'>{option.label}</span>
                  </CommandItem>
                )
              })}
              {showCreate && (
                <CommandItem
                  value={trimmedQuery}
                  onSelect={() => toggle(trimmedQuery)}
                >
                  <span className='size-3.5 shrink-0' />
                  Create &ldquo;{trimmedQuery}&rdquo;
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
